import { useLayoutEffect, useRef, useState } from "react";
import { BookOpen, Download, Eye, FileText, ListOrdered, LogOut, Menu, ScrollText, SkipForward, Trophy, Volume2, VolumeX, Settings, RotateCcw } from "lucide-react";
import GameLog from "@/components/game/GameLog";
import GlobalTemp from "@/components/game/GlobalTemp";
import FeedbackButton from "@/components/FeedbackButton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PLAYER_COLORS, PLAYER_ON_COLOR } from "@/lib/api";
import { useStageLayout } from "@/components/game/Stage";


export const SeaLevelIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="var(--sea)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 17c1.7 0 1.7-1.5 3.3-1.5S7 17 8.7 17s1.7-1.5 3.3-1.5S13.7 17 15.3 17 17 15.5 18.7 15.5 20.3 17 22 17" />
    <path d="M2 21c1.7 0 1.7-1.5 3.3-1.5S7 21 8.7 21s1.7-1.5 3.3-1.5S13.7 21 15.3 21 17 19.5 18.7 19.5 20.3 21 22 21" />
    <path d="M12 3v8" />
    <path d="m8.5 8 3.5 3.5L15.5 8" />
  </svg>
);

const KICK = { fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)", lineHeight: "11px" };

function RoundTrack({ round: rawRound, maxRounds }) {
  const round = Math.max(1, Math.min(rawRound, maxRounds));      // never "Round 13 / 12"
  return (
    <div data-testid="round-title" className="flex flex-col justify-center whitespace-nowrap shrink-0" title={`Round ${round} of ${maxRounds}`}>
      <span style={KICK}>Round</span>
      <span style={{ fontSize: 15, fontWeight: 700, lineHeight: "19px", color: "var(--ink)" }}>
        {round} <span style={{ color: "var(--ink-5)", fontWeight: 500 }}>/ {maxRounds}</span>
      </span>
    </div>
  );
}

// Fits its text into a fixed box: shrinks the font step by step until nothing overflows
// (never cut off with "…"). steps: [[bold px, small px], ...] largest first.
const FIT_STEPS = [[13.5, 11.5], [12.5, 11], [11.5, 10], [10.5, 9.5], [9.5, 8.5]];
function FitInstruction({ guide }) {
  const ref = useRef(null);
  const [step, setStep] = useState(0);
  const key = `${guide?.text || ""}|${guide?.sub || ""}`;
  useLayoutEffect(() => setStep(0), [key]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.scrollHeight > el.clientHeight + 1 && step < FIT_STEPS.length - 1) setStep(step + 1);
  });
  const [big, small] = FIT_STEPS[step];
  if (!guide) return <div className="flex-1 min-w-0" />;
  return (
    <div ref={ref} data-testid="instruction-bar" data-fit={step} aria-live="polite" className="flex-1 min-w-0 h-full flex flex-col justify-center overflow-hidden" style={{ gap: 1 }}>
      <span data-testid="instruction-text" data-muted={guide.muted ? "true" : "false"} style={{ fontSize: big, fontWeight: 700, lineHeight: 1.18, color: guide.muted ? "var(--ink-4)" : "var(--ink)" }}>
        {guide.text}
      </span>
      {guide.sub && (
        <span data-testid="instruction-sub" style={{ fontSize: small, lineHeight: 1.18, color: "var(--ink-3)" }}>
          {guide.sub}
        </span>
      )}
    </div>
  );
}

// Turn strip: pill in the active player's colour, the instruction text, END TURN at the right end.
function TurnStrip({ pill, guide, endTurn }) {
  return (
    <div data-testid="turn-strip" className="flex-1 min-w-0 h-[48px] flex items-center" style={{ gap: 10, padding: "0 6px 0 6px", borderRadius: 10, background: "var(--card)", border: "1px solid var(--line)" }}>
      {pill && (
        <span
          data-testid="turn-pill"
          data-color={pill.color || ""}
          className="shrink-0 whitespace-nowrap inline-flex items-center"
          style={{ height: 26, padding: "0 10px", borderRadius: 13, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", background: pill.color ? PLAYER_COLORS[pill.color] : "var(--sea)", color: pill.color ? PLAYER_ON_COLOR[pill.color] : "#fff", opacity: pill.muted ? 0.8 : 1 }}
        >
          {pill.text}
        </span>
      )}
      <FitInstruction guide={guide} />
      {endTurn?.visible && (
        <button
          type="button"
          data-testid="btn-end-turn"
          onClick={endTurn.onClick}
          disabled={!endTurn.enabled}
          title={endTurn.enabled ? "End your turn" : endTurn.reason || undefined}
          className="btn-primary btn-compact shrink-0"
          style={{ height: 36, padding: "0 16px", letterSpacing: "0.06em", fontWeight: 800, background: "var(--ink)", borderColor: "var(--ink)" }}
        >
          END TURN
        </button>
      )}
    </div>
  );
}

// Tiny wave glyph (kept for other callers).
export const WaveGlyph = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.5 10.5c1.6-3 3.2-3 4.8 0s3.2 3 4.7 0 3.2-3 4.8 0 3.2 3 4.7 0" />
    <path d="M2.5 16.5c1.6-3 3.2-3 4.8 0s3.2 3 4.7 0 3.2-3 4.8 0 3.2 3 4.7 0" opacity=".55" />
  </svg>
);

// Menu: score, game log, focus, rulebook, sound, settings, new game / leave, "This screen plays as".
function GameMenu({ phase, onSettings, onNewGame, players = [], seat, onSeatChange, online = false, over, onScore, onOpenLog, logCount, focusOn, onToggleFocus, onRulebook, muted, onToggleMute }) {
  const item = "h-10 gap-2 text-[14px] cursor-pointer";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="btn-ghost shrink-0" aria-label="Menu" data-testid="topbar-menu-btn" style={{ width: 38, height: 38, padding: 0, justifyContent: "center" }}>
          <Menu size={19} strokeWidth={1.9} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]" style={{ background: "var(--card)", borderColor: "var(--line)", boxShadow: "var(--shadow-card)" }}>
        <DropdownMenuLabel data-testid="phase-indicator" className="section-label" style={{ fontSize: 11 }}>
          Phase: {phase}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {!over && onScore && (
          <DropdownMenuItem onSelect={onScore} data-testid="open-live-score-btn" className={item}>
            <ListOrdered size={16} strokeWidth={1.8} /> Score
          </DropdownMenuItem>
        )}
        {onOpenLog && (
          <DropdownMenuItem onSelect={onOpenLog} data-testid="open-game-log-btn" className={item}>
            <ScrollText size={16} strokeWidth={1.8} /> Game log <span data-testid="game-log-count" className="ml-auto" style={{ color: "var(--ink-4)", fontSize: 13 }}>{logCount}</span>
          </DropdownMenuItem>
        )}
        {onToggleFocus && (
          <DropdownMenuItem onSelect={onToggleFocus} data-testid="focus-toggle-btn" data-active={focusOn ? "true" : "false"} className={item} title="Dim everything outside the current step">
            <Eye size={16} strokeWidth={1.8} /> Focus: {focusOn ? "on" : "off"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onRulebook} data-testid="open-rulebook-btn" className={item}>
          <BookOpen size={16} strokeWidth={1.8} /> Rulebook
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onToggleMute(); }} data-testid="sound-toggle-btn" aria-pressed={!muted} className={item}>
          {muted ? <VolumeX size={16} strokeWidth={1.8} /> : <Volume2 size={16} strokeWidth={1.8} />} Sound: {muted ? "off" : "on"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {onSettings && (
          <DropdownMenuItem onSelect={onSettings} data-testid="open-settings-btn" className={item} title="Host settings (between rounds only)">
            <Settings size={16} strokeWidth={1.8} /> Settings
          </DropdownMenuItem>
        )}
        {online ? (
          <DropdownMenuItem onSelect={onNewGame} data-testid="leave-game-btn" className={item}>
            <LogOut size={16} strokeWidth={1.8} /> Leave game
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={onNewGame} data-testid="new-game-btn" className={item}>
            <RotateCcw size={16} strokeWidth={1.8} /> New game
          </DropdownMenuItem>
        )}
        {onSeatChange && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="section-label" style={{ fontSize: 11 }}>
              This screen plays as
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup value={seat || "shared"} onValueChange={(v) => onSeatChange(v === "shared" ? null : v)}>
              <DropdownMenuRadioItem value="shared" data-testid="seat-option-shared" className="h-10 text-[14px] cursor-pointer">
                Shared screen
              </DropdownMenuRadioItem>
              {players.map((p, i) => (
                <DropdownMenuRadioItem key={p.color} value={p.color} data-testid={`seat-option-${p.color}`} className="h-10 gap-2 text-[14px] cursor-pointer">
                  <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: PLAYER_COLORS[p.color] }} />
                  {i + 1} · {p.name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// LAYOUT F top bar (58px): Porosity · turn strip (pill, instruction, END TURN) · SEA LEVEL RISE ·
// ROUND · WEATHER chip · Feedback · menu. The game log, score, focus, rulebook and sound live in the menu.
// After the game ends: "Game over", Show scores and the two downloads.
export default function TopBar({ game, rules, maxRounds, isSetup, setupTitle, setupText, guide, pill, endTurn, weatherChip, muted, onToggleMute, onScore, onRulebook, onSettings, onNewGame, logOpen, onLogOpenChange, focusOn = true, onToggleFocus, seat, onSeatChange, online = false, skipName = null, onSkip, onShowScores, onDownloadImage, onDownloadLog, downloading = false }) {
  const over = !!game.game_over;
  const { compact } = useStageLayout();
  return (
    <header
      data-testid="top-bar"
      className={`shrink-0 h-[58px] flex flex-nowrap items-center overflow-hidden ${compact ? "px-3 gap-x-3" : "px-4 gap-x-4"}`}
      style={{ background: "var(--surface)", borderBottom: "1px solid var(--line)" }}
    >
      <span className="whitespace-nowrap shrink-0" style={{ fontSize: compact ? 18 : 20, fontWeight: 700, color: "var(--ink)" }} data-testid="topbar-title">Porosity</span>
      {over ? (
        <span data-testid="game-over-title" className="flex-1 whitespace-nowrap" style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>
          Game over
        </span>
      ) : (
        <TurnStrip pill={pill} guide={isSetup && !guide ? { text: setupTitle, sub: setupText } : guide} endTurn={endTurn} />
      )}
      {skipName && onSkip && (
        <button type="button" onClick={onSkip} className="btn-ghost shrink-0" data-testid="skip-gone-btn" title={`${skipName} has been gone a while. Do their step for them.`}>
          <SkipForward size={16} strokeWidth={1.8} /> Skip {skipName}
        </button>
      )}
      {over && onShowScores && (
        <button type="button" onClick={onShowScores} className="btn-ghost shrink-0" data-testid="show-scores-btn" title="Show the final scores" style={{ color: "var(--sea)", fontWeight: 600 }}>
          <Trophy size={16} strokeWidth={1.8} /> Show scores
        </button>
      )}
      {over && onDownloadImage && (
        <button type="button" onClick={onDownloadImage} disabled={downloading} className="btn-ghost shrink-0" data-testid="topbar-download-image-btn" title="Download board image (PNG of the final board, players and scores)" aria-label="Download board image">
          <Download size={16} strokeWidth={1.8} /> {!compact && "Board image"}
        </button>
      )}
      {over && onDownloadLog && (
        <button type="button" onClick={onDownloadLog} className="btn-ghost shrink-0" data-testid="topbar-download-log-btn" title="Download game log (text file)" aria-label="Download game log">
          <FileText size={16} strokeWidth={1.8} /> {!compact && "Game log"}
        </button>
      )}
      {!over && !isSetup && <GlobalTemp game={game} />}
      {!over && isSetup && (
        <div data-testid="round-title" className="flex flex-col justify-center whitespace-nowrap shrink-0">
          <span style={KICK}>Setup</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>{setupTitle}</span>
        </div>
      )}
      {!isSetup && <RoundTrack round={over ? (game.rounds_played ?? game.round - 1) : game.round} maxRounds={maxRounds} />}
      {!isSetup && weatherChip}
      {!isSetup && <GameLog entries={game.game_log} open={logOpen} onOpenChange={onLogOpenChange} trigger={false} viewer={online ? null : seat || game.players?.[game.current_player_index]?.color || null} />}
      <FeedbackButton compact={compact} />
      <GameMenu phase={game.phase} onSettings={onSettings} onNewGame={onNewGame} players={game.players} seat={seat} onSeatChange={onSeatChange} online={online} over={over} onScore={onScore} onOpenLog={isSetup ? null : () => onLogOpenChange(true)} logCount={(game.game_log || []).length} focusOn={focusOn} onToggleFocus={onToggleFocus} onRulebook={onRulebook} muted={muted} onToggleMute={onToggleMute} />
    </header>
  );
}
